import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Calendar, Users } from "lucide-react";
import { getPriceFrom } from "@/data/trips";

export default function TripCard({ trip, index = 0 }) {
  const priceFrom = getPriceFrom(trip);
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay: index * 0.08, ease: "easeOut" }}
      className="card-soft group overflow-hidden flex flex-col"
      data-testid={`trip-card-${trip.slug}`}
    >
      <Link
        to={`/viajes-familia-esquiar/${trip.slug}`}
        className="block relative aspect-[4/3] overflow-hidden bg-brand-surface"
      >
        <img
          src={trip.hero_image || trip.image}
          alt={`Viaje familiar a ${trip.short_name} — ${trip.season}`}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/0 to-black/0" />
        {trip.sold_out && (
          <>
            <div className="absolute inset-0 bg-gradient-to-t from-brand-primary/85 via-brand-primary/40 to-brand-primary/20" />
            <div className="absolute inset-0 flex items-center justify-center p-6">
              <div
                data-testid={`trip-card-soldout-${trip.slug}`}
                className="relative -rotate-[4deg] transition-transform duration-500 group-hover:-rotate-[2deg] group-hover:scale-[1.03]"
              >
                <span className="absolute inset-0 translate-x-2 translate-y-2 bg-brand-ink/40 blur-[3px]" aria-hidden="true" />
                <div className="relative border border-white/50 p-[5px] shadow-[0_22px_48px_-20px_rgba(0,0,0,0.7)]">
                  <div className="border border-brand-primary/15 bg-[#FBF8F3] px-7 py-4 text-center">
                    <span className="block font-cormorant italic text-[11px] uppercase tracking-[0.32em] text-brand-accent">
                      Temporada 26 · 27
                    </span>
                    <span className="mt-1.5 block font-outfit text-2xl md:text-[26px] font-semibold uppercase leading-none tracking-[0.2em] text-brand-primary">
                      Completo
                    </span>
                    <span className="mx-auto mt-3 flex items-center justify-center gap-2" aria-hidden="true">
                      <span className="h-px w-8 bg-brand-accent/50" />
                      <span className="h-1 w-1 rotate-45 bg-brand-accent/70" />
                      <span className="h-px w-8 bg-brand-accent/50" />
                    </span>
                    <span className="mt-2.5 block text-[10px] uppercase tracking-[0.22em] text-brand-muted">
                      Plazas agotadas
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-white/90 backdrop-blur px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-brand-primary">
          {trip.season}
        </span>
      </Link>

      <div className="p-6 md:p-7 flex flex-col gap-4 flex-1">
        <div>
          <h3 className="font-outfit text-2xl font-semibold text-brand-primary leading-tight">
            {trip.name}
          </h3>
          <p className="mt-2 text-sm text-brand-muted flex items-center gap-2">
            <Calendar className="h-3.5 w-3.5 text-brand-accent" /> {trip.dates_short} · {trip.duration}
          </p>
        </div>

        <p className="font-manrope text-sm text-brand-ink/80 leading-relaxed line-clamp-3">
          {trip.intro}
        </p>

        <div className="mt-auto pt-4 border-t border-brand-line flex items-end justify-between gap-4">
          <div>
            {trip.sold_out ? (
              <>
                <p className="text-[11px] uppercase tracking-wider text-brand-accent font-semibold">
                  Plazas agotadas
                </p>
                <p className="font-outfit text-2xl text-brand-primary font-semibold">Completo</p>
                <p className="text-xs text-brand-muted mt-0.5">
                  Consúltanos otras fechas disponibles
                </p>
              </>
            ) : (
              <>
                <p className="text-[11px] uppercase tracking-wider text-brand-muted">Desde</p>
                <p className="font-outfit text-2xl text-brand-primary font-semibold">
                  {priceFrom.amount.toLocaleString("es-ES")} €
                </p>
                {priceFrom.label && (
                  <p className="text-xs text-brand-muted mt-0.5" data-testid={`trip-card-price-cat-${trip.slug}`}>
                    por {priceFrom.label.toLowerCase()}
                    {priceFrom.adult ? ` · adulto ${priceFrom.adult.toLocaleString("es-ES")} €` : ""}
                  </p>
                )}
                <p className="text-xs text-brand-muted flex items-center gap-1.5 mt-0.5">
                  <Users className="h-3 w-3" /> {trip.family_basis}
                </p>
              </>
            )}
          </div>
          <Link
            to={`/viajes-familia-esquiar/${trip.slug}`}
            data-testid={`trip-card-cta-${trip.slug}`}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-primary hover:text-brand-accent transition-colors"
          >
            Ver viaje <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </motion.article>
  );
}
